import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  downloadFinanceCsv,
  ICurrencyMoneyModel,
  IFinanceCsvHeaders,
} from 'core/utils/exportCsv';
import { buildStudentMoney } from 'core/utils/studentMoney';

import { IStudent } from './types';
import { StudentColors } from './studentColors';

interface IFinanceExportButtonProps {
  student: IStudent;
  /** Finance-view permission. When false the control is not rendered (Req 21.5). */
  canExportFinance: boolean;
}

/**
 * Student finance-summary export (Area 8, Req 21.2/21.5/21.6).
 *
 * Maps the student's per-currency `ICurrencyTotals` (`buildStudentMoney`) onto
 * the shared guarded finance CSV (Budget→planned/Paid/Debt) with localized
 * headers, reusing `downloadFinanceCsv` so the permission guard blocks output
 * even if the control leaked (Req 21.6). Hidden when finance-view is denied.
 */
export const FinanceExportButton = memo(
  ({ student, canExportFinance }: IFinanceExportButtonProps) => {
    const { t } = useTranslation();

    const onExport = useCallback(() => {
      const model: ICurrencyMoneyModel[] = buildStudentMoney(
        student.agreements || [],
      ).map((row) => ({
        currency: row.currency,
        planned: row.budget,
        issued: null,
        paid: row.paid,
        debt: row.debt,
        overdueCount: 0,
        upcomingCount: 0,
        hasInvoiceData: false,
      }));

      const headers: IFinanceCsvHeaders = {
        currency: t('table.students.currency'),
        planned: t('table.students.budgetAmount'),
        issued: t('table.students.budgetAmount'),
        paid: t('table.students.paidAmount'),
        debt: t('table.students.debt'),
        overdue: t('table.students.debt'),
        upcoming: t('table.students.debt'),
      };

      downloadFinanceCsv(
        model,
        headers,
        canExportFinance,
        'student-finance.csv',
      );
    }, [student.agreements, canExportFinance, t]);

    if (!canExportFinance) return null;

    return (
      <Button type="button" onClick={onExport}>
        {t('pages.studentDetail.exportFinance')}
      </Button>
    );
  },
);

const Button = styled.button`
  border: 1px solid ${StudentColors.border};
  border-radius: 8px;
  background: ${StudentColors.surface};
  color: ${StudentColors.accent};
  font: inherit;
  font-size: 13px;
  padding: 6px 12px;
  cursor: pointer;

  &:hover {
    background: ${StudentColors.accentSurface};
  }
`;
